import { FC, useEffect, useMemo, useState } from 'react'
import classNames from 'classnames'
import { Checkbox } from 'antd'
import { DragOutlined } from '@ant-design/icons'
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from '@dnd-kit/modifiers'
import { useSelector } from 'react-redux'
import Modal from '@common/Modal'
import Button from '@UI/New/Button'
import Preloader from '@UI/Preloader/Preloader'
import FieldWrapper from '@pages/Admin/CourseSettings/components/FieldWrapper/FieldWrapper'
import SecondaryButton from '@pages/Admin/CourseSettings/components/SecondaryButton/SecondaryButton'
import { useAddAlert } from '@/hooks/useAddAlert'
import { IcoRemove } from '@/utils/icons'
import { IRole } from '@pages/Admin/Reports/CourseAnalytics/utils/types'
import mclasses from '@pages/Admin/CRM/styles/modal.module.css'
import cn from './index.module.css'
import {
  changeSetting,
  SettingsParams,
} from '@pages/Admin/AllUsers/StudentsTable/SettingsHelper'
import { SETTINGS_TYPES } from '@/constant'
import { fethSettingsStore } from '@/services/SettingsStoreUpdateService'
import {
  createRoleSettings,
  DEFAULT_ROLE_KEY,
  EMPTY_ROLE_DEFAULT_COLUMNS,
  IRoleBasedTableRoleSettings,
  IRoleBasedTableSettings,
  parseRoleBasedTableSettings,
  RoleDefaultColumns,
} from './utils'

interface IRoleBasedTableSettingsModal {
  open: boolean
  onDismiss: () => void
  title?: string
  settingKey: string
  columnsMap: { value: string; label: string }[]
  description?: string
  rolesLabel?: string
  acceptedRoles?: string[]
  defaultRoleKey?: string
  roleDefaultColumns?: RoleDefaultColumns
  excludedColumnsByRole?: Record<string, string[]>
}

const DEFAULT_ACCEPTED_ROLES = ['admin', 'seminarian']

const sortByOrder = (a: IRole, b: IRole) =>
  (a.order ?? Infinity) - (b.order ?? Infinity)

const RoleBasedTableSettingsModal: FC<IRoleBasedTableSettingsModal> = (
  props,
) => {
  return (
    <Modal
      active={props.open}
      className={classNames(mclasses.modalWrapper, cn.modalWrapper) as any}
      setActive={props.onDismiss}
    >
      <ModalBody {...props} />
    </Modal>
  )
}

const ModalBody: FC<IRoleBasedTableSettingsModal> = ({
  onDismiss,
  title = 'Настройка таблицы',
  settingKey,
  columnsMap,
  description = 'Выберите, какие колонки видны для роли, и задайте их порядок',
  rolesLabel = 'Настройки для каждой роли',
  acceptedRoles = DEFAULT_ACCEPTED_ROLES,
  defaultRoleKey = DEFAULT_ROLE_KEY,
  roleDefaultColumns = EMPTY_ROLE_DEFAULT_COLUMNS,
  excludedColumnsByRole,
}) => {
  const addAlert = useAddAlert()
  const defaultColumns = useMemo(
    () => columnsMap.map((item) => item.value),
    [columnsMap],
  )

  const { roles, storedSettings } = useSelector((store: any) => {
    const data = store?.allRoles?.data ?? []
    const rawSettings = store?.settings?.custom?.[settingKey]

    return {
      roles: (data as IRole[])
        .filter((role) => role.is_active && acceptedRoles.includes(role.role))
        .sort(sortByOrder),
      storedSettings: parseRoleBasedTableSettings({
        value: rawSettings,
        settingKey,
        defaultColumns,
        defaultRoleKey,
        roleDefaultColumns,
      }),
    }
  })

  const [form, setForm] = useState<{
    selectedRole: IRole
    settings: IRoleBasedTableSettings
  } | null>(null)

  useEffect(() => {
    if (!roles?.[0]) return

    setForm((prev) => {
      const nextSelectedRole =
        roles.find((role) => role.role === prev?.selectedRole?.role) ?? roles[0]

      return {
        selectedRole: nextSelectedRole,
        settings: storedSettings,
      }
    })
  }, [roles.length, JSON.stringify(storedSettings)])

  const currentSettings = useMemo(() => {
    if (!form) return null

    return (
      form.settings.forRoles.find((it) => it.role === form.selectedRole.role) ??
      createRoleSettings(
        form.selectedRole.role,
        roleDefaultColumns[form.selectedRole.role] ??
        (
          storedSettings?.forRoles?.find((it) => it.role === defaultRoleKey) ??
          createRoleSettings(defaultRoleKey, defaultColumns)
        ).columns,
      )
    )
  }, [
    defaultColumns,
    defaultRoleKey,
    form,
    roleDefaultColumns,
    storedSettings?.forRoles,
  ])

  const onSelectRole = (role: IRole) => {
    if (!form) return

    setForm((prev) => (prev ? { ...prev, selectedRole: role } : prev))
  }

  const onToggleField = (column: string) => {
    if (!form) return

    const currentRoleConfig = form.settings.forRoles.find(
      (r) => r.role === form.selectedRole.role,
    ) ??
      currentSettings ?? {
        role: form.selectedRole.role,
        columns: defaultColumns,
      }

    const updatedRoleConfig: IRoleBasedTableRoleSettings = {
      ...currentRoleConfig,
      columns: currentRoleConfig.columns.find((it) => it === column)
        ? currentRoleConfig.columns.filter((it) => it !== column)
        : [...currentRoleConfig.columns, column],
    }

    setForm((prev) => ({
      ...prev!,
      settings: {
        ...prev!.settings,
        forRoles: [
          ...prev!.settings.forRoles.filter(
            (r) => r.role !== form.selectedRole.role,
          ),
          updatedRoleConfig,
        ],
      },
    }))
  }

  const saveSettings = () => {
    if (!form?.settings) return

    const params: SettingsParams = {
      key: settingKey,
      value: JSON.stringify(excludedColumnsByRole ? {
        ...form.settings,
        forRoles: form.settings.forRoles.map((roleSettings) => ({
          ...roleSettings,
          columns: roleSettings.columns.filter((column) =>
            defaultColumns.includes(column) &&
            !excludedColumnsByRole[roleSettings.role]?.includes(column),
          ),
        })),
      } : form.settings),
      type: SETTINGS_TYPES.CUSTOM,
    }

    changeSetting(
      params,
      () => {
        fethSettingsStore()
        addAlert({ title: 'Настройки сохранены' })
        onDismiss()
      },
      () => {
        addAlert({
          title: 'Ошибка при сохранении настроек',
          type: 'error',
        })
      },
    )
  }

  const availableColumns = columnsMap.filter((column) =>
    !excludedColumnsByRole?.[form?.selectedRole.role ?? '']?.includes(column.value),
  )
  const enabled = !currentSettings
    ? []
    : currentSettings.columns
        .map((item) => availableColumns.find((it) => it.value === item))
        .filter((it): it is NonNullable<typeof it> => it !== undefined)

  const disabled = availableColumns.filter(
    (it) => !currentSettings?.columns?.includes(it.value),
  )

  const orderedFields = [...enabled, ...disabled]

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {}),
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (!over || active.id === over.id || !currentSettings || !form) return

    const oldIndex = currentSettings.columns.indexOf(active.id as string)
    const newIndex = currentSettings.columns.indexOf(over.id as string)

    if (oldIndex === -1 || newIndex === -1) return

    const newItems = arrayMove(currentSettings.columns, oldIndex, newIndex)

    const currentRoleConfig = form.settings.forRoles.find(
      (r) => r.role === form.selectedRole.role,
    ) ?? {
      role: form.selectedRole.role,
      columns: currentSettings?.columns ?? defaultColumns,
    }

    const updatedRoleConfig: IRoleBasedTableRoleSettings = {
      ...currentRoleConfig,
      columns: newItems,
    }

    setForm((prev) => ({
      ...prev!,
      settings: {
        ...prev!.settings,
        forRoles: [
          ...prev!.settings.forRoles.filter(
            (r) => r.role !== form.selectedRole.role,
          ),
          updatedRoleConfig,
        ],
      },
    }))
  }

  return (
    <div className={mclasses.modalContainer}>
      <div className={mclasses.modalHeader}>
        <div className={mclasses.modalTitleContainer}>
          <span className={mclasses.modalHeaderTitle}>{title}</span>
        </div>
        <div className={mclasses.closeIcon} onClick={onDismiss}>
          <IcoRemove />
        </div>
      </div>
      {!form ? (
        <div className={cn.loading}>
          <Preloader />
        </div>
      ) : (
        <div className={classNames(mclasses.formWrapper, cn.form)}>
          <FieldWrapper label={rolesLabel} description={description}>
            <div className={cn.rolesList}>
              {roles.map((role) => {
                const isSelected = role.role === form.selectedRole.role

                return (
                  <SecondaryButton
                    key={role.id}
                    onClick={() => onSelectRole(role)}
                    className={classNames(
                      cn.roleButton,
                      isSelected && cn.roleButtonSelected,
                    )}
                  >
                    {role.name}
                  </SecondaryButton>
                )
              })}
            </div>
          </FieldWrapper>

          <div className={cn.settingsList}>
            <div className={cn.settingsRowHeader}>
              <div className={cn.settingsHead}>Колонка</div>
              <div className={cn.settingsHead}>Активна</div>
              <div className={cn.settingsHead}></div>
            </div>

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
              modifiers={[restrictToVerticalAxis, restrictToParentElement]}
            >
              <SortableContext
                items={enabled.map((it) => it.value)}
                strategy={verticalListSortingStrategy}
              >
                {orderedFields.map((item) => (
                  <SortableCheckboxRow
                    key={item.value}
                    item={item}
                    enabled={!!enabled.find((it) => it.value === item.value)}
                    onChange={() => onToggleField(item.value)}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </div>
        </div>
      )}

      <div className={mclasses.actions}>
        <Button color="gray" onClick={onDismiss}>
          Отменить
        </Button>
        <Button color="secondary" onClick={saveSettings}>
          Сохранить
        </Button>
      </div>
    </div>
  )
}

const SortableCheckboxRow = ({
  item,
  enabled,
  onChange,
}: {
  item: {
    value: string
    label: string
  }
  enabled: boolean
  onChange: () => void
}) => {
  const {
    setNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.value })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div
      ref={enabled ? setNodeRef : undefined}
      className={cn.settingsRow}
      style={style}
      {...attributes}
    >
      <div className={cn.settingsCell}>{item.label}</div>
      <div className={cn.settingsCell}>
        <Checkbox checked={enabled} onChange={onChange} />
      </div>
      {enabled ? (
        <div className={cn.settingsCell}>
          <DragOutlined
            className={classNames(cn.iconButton, cn.dragIcon)}
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
            {...listeners}
          />
        </div>
      ) : (
        <div className={cn.settingsCell} />
      )}
    </div>
  )
}

export default RoleBasedTableSettingsModal
